import { useRenameFileRecord } from "@/api/fileRecords";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useFolderCascade } from "@/pages/workspaces/hooks/useFolderCascade";
import { cn } from "@/utils";
import type { FileRecord, MoveFileRecordPayload } from "@cohesive/api-client";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

interface RenameFileRecordDialogProps {
  record: FileRecord | null;
  onClose: () => void;
  /** Called when rename succeeds, before `onClose`. */
  onSuccess?: () => void;
  /**
   * Full record list for the scope. Required so a folder rename can
   * cascade descendants — otherwise the renamed folder ends up empty and
   * the original children dangle at their old paths.
   */
  allRecords: FileRecord[];
}

function getFileName(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

function getParentPath(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const lastSlash = cleaned.lastIndexOf("/");
  if (lastSlash === -1) return "";
  return cleaned.slice(0, lastSlash + 1);
}

/**
 * Split a file name into the editable stem and the locked extension.
 * Mirrors macOS Finder / Drive: only the *last* `.ext` is locked, dotfiles
 * (`.env`, `.gitignore`) have no extension, and files with no dot are
 * fully editable.
 *
 * Examples:
 *   "report.pdf"      -> { stem: "report",  ext: ".pdf" }
 *   "archive.tar.gz"  -> { stem: "archive.tar", ext: ".gz" }
 *   ".env"            -> { stem: ".env",   ext: "" }
 *   "Makefile"        -> { stem: "Makefile", ext: "" }
 */
function splitFileName(name: string): { stem: string; ext: string } {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) {
    return { stem: name, ext: "" };
  }
  return { stem: name.slice(0, lastDot), ext: name.slice(lastDot) };
}

export function buildRenameDestination(
  record: FileRecord,
  path: string,
  isFolder: boolean,
): MoveFileRecordPayload["destination"] {
  return {
    class: record.class,
    workspace_id: record.workspace_id,
    canvas_id: record.canvas_id,
    flow_session_id: record.flow_session_id,
    path,
    is_folder: isFolder || undefined,
  };
}

export function RenameFileRecordDialog({
  record,
  onClose,
  onSuccess,
  allRecords,
}: RenameFileRecordDialogProps) {
  const path = record?.path;
  const isFolder = path?.endsWith("/") ?? false;
  const initialName = path ? getFileName(path) : "";
  const { ext } = !isFolder ? splitFileName(initialName) : { ext: "" };

  const [stem, setStem] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!record) return;
    if (!record.path) {
      toast.error("Could not rename this file.");
      onClose();
      return;
    }
    const name = getFileName(record.path);
    const split = record.path.endsWith("/")
      ? { stem: name, ext: "" }
      : splitFileName(name);
    setStem(split.stem);
    // Pre-select the stem so the user can immediately type a replacement.
    // RAF gives the autoFocus a frame to settle before we set the range.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(0, split.stem.length);
      }
    });
  }, [record?.id]);

  const cascade = useFolderCascade();

  const renameFileRecord = useRenameFileRecord();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedStem = stem.trim();
  const isValid = trimmedStem.length > 0 && !trimmedStem.includes("/");

  const handleSubmit = async () => {
    if (!record?.path || !isValid) return;
    const parent = getParentPath(record.path);
    const newName = `${trimmedStem}${ext}`;
    const newPath = isFolder ? `${parent}${newName}/` : `${parent}${newName}`;

    setIsSubmitting(true);
    try {
      // Folder rename cascades — children share the folder's path prefix
      // and need to be rewritten to the new prefix or they'll dangle at
      // the old location while the renamed folder appears empty.
      await cascade.moveRecordCascading({
        record,
        payload: {
          destination: buildRenameDestination(record, newPath, !!isFolder),
        },
        allRecords,
        moveOne: ({ fileRecordId, payload }) => {
          const sourceRecord =
            fileRecordId === record.id
              ? record
              : allRecords.find((r) => r.id === fileRecordId);
          if (!sourceRecord) {
            throw new Error("Rename source record not found");
          }
          return renameFileRecord.mutateAsync({
            record: sourceRecord,
            newPath: payload.destination.path,
          });
        },
      });
      toast.success(isFolder ? "Folder renamed" : "File renamed");
      onSuccess?.();
      onClose();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      toast.error(
        status === 409
          ? `Something named "${newName}" already exists here.`
          : "Failed to rename",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={record !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename {isFolder ? "Folder" : "File"}</DialogTitle>
          <DialogDescription>
            {ext
              ? "Enter a new name. The file extension is preserved."
              : "Enter a new name."}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {ext ? (
            <div
              className={cn(
                "flex h-10 w-full rounded-md border border-input bg-background text-sm ring-offset-background",
                "focus-within:outline-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
              )}
            >
              <input
                ref={inputRef}
                value={stem}
                onChange={(e) => setStem(e.target.value)}
                placeholder={isFolder ? "Folder name" : "File name"}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSubmit();
                }}
                className="flex-1 min-w-0 bg-transparent pl-3 py-2 outline-none placeholder:text-muted-foreground"
              />
              <span
                aria-hidden
                className="flex items-center pr-3 text-muted-foreground select-none"
              >
                {ext}
              </span>
            </div>
          ) : (
            <Input
              ref={inputRef}
              autoFocus
              value={stem}
              onChange={(e) => setStem(e.target.value)}
              placeholder={isFolder ? "Folder name" : "File name"}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
